'use client';

import { Stack, Typography } from '@mui/material';
import Image from 'next/image';

import { icons } from '@/assets/icons';
import complianceImage from '@/assets/materials/orbit/compliance-feature-image.png';
import corImage from '@/assets/materials/orbit/cor-feature-image.png';
import eorImage from '@/assets/materials/orbit/eor-feature-image.png';
import payrollImage from '@/assets/materials/orbit/payroll-feature-image.png';
import web3Image from '@/assets/materials/orbit/web3-feature-image.png';
import { CoreLaunchPadCard } from '@/components/bases/CoreLaunchPadCard';

const FEATURES = [
  {
    id: 1,
    title: 'Hire Employees Globally Without Entities',
    icon: icons.ic_global_2,
    image: eorImage,
    features: [
      'Full-time employees in 180+ countries',
      'We act as the legal employer',
      'Payroll, tax, and labor compliance handled',
      'Benefits and health insurance where applicable',
    ],
    imageFirst: false,
  },
  {
    id: 2,
    title: 'Compliant Global Contractors',
    icon: icons.ic_profile_2user,
    image: corImage,
    features: [
      'Contractor onboarding and agreements',
      'Reduced misclassification risk',
      'Global payments in fiat and digital assets',
      'Work visas handled per case',
    ],
    imageFirst: true,
  },
  {
    id: 3,
    title: 'Global Payroll, Done Right',
    icon: icons.ic_cards,
    image: payrollImage,
    features: [
      'Multi-country payroll',
      'Local tax withholding',
      'Multi-currency payouts',
      'Fiat or digital-asset funding',
    ],
    imageFirst: false,
  },
  {
    id: 4,
    title: 'Built for Modern, Web3-Native Teams',
    icon: icons.ic_flash,
    image: web3Image,
    features: [
      'Remote-first by design',
      'Flexible compensation structures',
      'Digital-asset-friendly funding',
      'No rigid enterprise systems',
    ],
    imageFirst: true,
  },
  {
    id: 5,
    title: 'Web3-Friendly, Regulation-Aligned',
    icon: icons.ic_shield_tick,
    image: complianceImage,
    features: [
      'Local labor law alignment',
      'Tax and statutory reporting',
      'Audit-ready records',
      'Jurisdiction-aware structure',
    ],
    imageFirst: false,
  },
];

const FeatureCard = ({ feature }: { feature: (typeof FEATURES)[0] }) => {
  return (
    <Stack
      direction={{ xs: 'column-reverse', lg: feature.imageFirst ? 'row' : 'row-reverse' }}
      alignItems={'center'}
      gap={{ xs: 4, sm: 10, lg: 20 }}
      width={'100%'}>
      {/* Image */}
      <Stack
        width={{ xs: '100%', sm: '100%', md: '620px' }}
        height={{ xs: '250px', sm: '320px', md: '440px' }}
        position={'relative'}
        sx={{ flexShrink: 0 }}>
        <Image src={feature.image} alt={feature.title} fill style={{ objectFit: 'cover', borderRadius: '8px' }} />
      </Stack>

      {/* Content */}
      <Stack gap={{ xs: 3, sm: 4, md: 5 }} width={'100%'} flex={1}>
        <CoreLaunchPadCard item={feature} />

        <Typography
          component={'h3'}
          fontSize={{ xs: '24px', sm: '28px', md: '32px' }}
          lineHeight={{ xs: '33px', sm: '38px', md: '44px' }}
          fontWeight={700}
          className={'clip-text'}
          sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
          {feature.title}
        </Typography>

        <Stack gap={2}>
          {feature.features.map((item, index) => (
            <Stack key={index} direction={'row'} alignItems={'center'} gap={3}>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <mask id='path-1-inside-1_1783_5310' fill='white'>
                  <path d='M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z' />
                </mask>
                <path
                  d='M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z'
                  fill='#7296FF'
                  fillOpacity='0.2'
                />
                <path
                  d='M12 24V23C5.92487 23 1 18.0751 1 12H0H-1C-1 19.1797 4.8203 25 12 25V24ZM24 12H23C23 18.0751 18.0751 23 12 23V24V25C19.1797 25 25 19.1797 25 12H24ZM12 0V1C18.0751 1 23 5.92487 23 12H24H25C25 4.8203 19.1797 -1 12 -1V0ZM12 0V-1C4.8203 -1 -1 4.8203 -1 12H0H1C1 5.92487 5.92487 1 12 1V0Z'
                  fill='#7296FF'
                  fillOpacity='0.5'
                  mask='url(#path-1-inside-1_1783_5310)'
                />
                <path
                  d='M17.3337 8L10.0003 15.3333L6.66699 12'
                  stroke='#7296FF'
                  strokeWidth='1.33333'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>

              <Typography
                fontSize={{ xs: '14px', sm: '16px', md: '18px' }}
                variant={'bodyLarge'}
                color={'#FFFFFF'}
                flex={1}>
                {item}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const FeatureSections = () => {
  return (
    <Stack
      id='why-orbit'
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      paddingX={{ xs: '24px', sm: '40px', md: '80px' }}
      paddingY={{ xs: '32px', sm: '50px', md: '80px' }}
      alignItems={'center'}
      component={'section'}
      position={'relative'}>
      <Stack gap={{ xs: 6, sm: 12, md: 20 }} width={'min(1280px, 100%)'} zIndex={2}>
        {FEATURES.map(feature => (
          <FeatureCard key={feature.id} feature={feature} />
        ))}
      </Stack>
    </Stack>
  );
};
